import { AbstractValidatedClass, Joi, ValidatedClass, constraint } from '@verclam/form-validator';
import type OrganizationView from '@UI/Store/Organizations/Views/OrganizationView';

type OrganizationFormArgs = {
	name: string;
	siret: string | null;
	headOfficeAddress: string | null;
	documentFooterMentions: string | null;
};

/**
 * `siret`, `headOfficeAddress` and `documentFooterMentions` are optional: only their maximum length
 * is constrained, matching the server's `CreateOrganizationDto`/`UpdateOrganizationDto`.
 */
@ValidatedClass({ ignoreNullValue: true })
class OrganizationFormData extends AbstractValidatedClass<OrganizationFormArgs> {
	@constraint(Joi.string().max(180), () => 'Le nom ne peut pas dépasser 180 caractères.')
	@constraint(Joi.string().required(), () => 'Le nom est obligatoire.')
	public readonly name: string;

	@constraint(Joi.string().max(20).allow(''), () => 'Le SIRET ne peut pas dépasser 20 caractères.')
	public readonly siret: string | null;

	@constraint(
		Joi.string().max(255).allow(''),
		() => 'L’adresse du siège ne peut pas dépasser 255 caractères.',
	)
	public readonly headOfficeAddress: string | null;

	@constraint(
		Joi.string().max(2000).allow(''),
		() => 'Les mentions ne peuvent pas dépasser 2000 caractères.',
	)
	public readonly documentFooterMentions: string | null;

	constructor(data: OrganizationFormArgs) {
		super(data);
		this.name = data.name;
		this.siret = data.siret;
		this.headOfficeAddress = data.headOfficeAddress;
		this.documentFooterMentions = data.documentFooterMentions;
		Object.freeze(this);
	}

	static createEmpty(): OrganizationFormData {
		return new OrganizationFormData({
			name: '',
			siret: null,
			headOfficeAddress: null,
			documentFooterMentions: null,
		});
	}

	static fromView(organization: OrganizationView): OrganizationFormData {
		return new OrganizationFormData({
			name: organization.name,
			siret: organization.siret,
			headOfficeAddress: organization.headOfficeAddress,
			documentFooterMentions: organization.documentFooterMentions,
		});
	}

	setName(name: string): this {
		return this.cloneWith({ name } as Partial<OrganizationFormArgs>);
	}

	setSiret(siret: string): this {
		return this.cloneWith({ siret } as Partial<OrganizationFormArgs>);
	}

	setHeadOfficeAddress(headOfficeAddress: string): this {
		return this.cloneWith({ headOfficeAddress } as Partial<OrganizationFormArgs>);
	}

	setDocumentFooterMentions(documentFooterMentions: string): this {
		return this.cloneWith({ documentFooterMentions } as Partial<OrganizationFormArgs>);
	}
}

export default OrganizationFormData;
